import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { trackEvent } from 'helpers/analytics';
import { useGetSignupFormQuery, useUpdateSignupFormMutation } from 'services/signupForms';
import { transformApiFieldsToInternal } from '../../utils/transformers';

export function useFormBuilder({ collectionId, sharedListId }) {
  const {
    data: formConfig,
    isLoading,
    error,
  } = useGetSignupFormQuery({ collectionId, sharedListId }, { skip: !collectionId || !sharedListId });
  const [updateConfig, { isLoading: isSaving }] = useUpdateSignupFormMutation();
  const [formState, setFormState] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [lastAddedFieldId, setLastAddedFieldId] = useState(null);

  useEffect(() => {
    if (formConfig && !formState) {
      const fields = transformApiFieldsToInternal(formConfig.signupFormFields);
      setFormState({
        title: formConfig.title || '',
        description: formConfig.description || '',
        fields,
        submitButtonText: formConfig.submitButtonText || 'Join now',
        successMessageTitle: formConfig.successMessageTitle || 'Thanks for joining!',
        successMessageDescription:
          formConfig.successMessageDescription || "We'll get in touch when there are relevant opportunities.",
      });
    }
  }, [formConfig, formState]);

  const updateFormState = useCallback((updates) => {
    setFormState((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  }, []);

  // Field operations
  const addField = useCallback(
    (fieldType) => {
      if (!formState) return;

      const newFieldId = uuidv4();
      const newField = {
        id: newFieldId,
        type: fieldType,
        label: '',
        required: false,
        order: formState.fields.length,
        ...(fieldType === 'select'
          ? { options: [{ id: `opt-${Date.now()}`, value: '', label: '' }], allowMultiple: false }
          : {}),
        ...(fieldType === 'checkbox' ? { options: [], allowMultiple: false } : {}),
      };
      updateFormState({
        fields: [...formState.fields, newField],
      });
      setLastAddedFieldId(newFieldId);

      trackEvent('contacts:list:signup_form_field_added', {
        field_type: fieldType,
        shared_list_id: sharedListId,
        form_id: formConfig?.id,
      });
    },
    [formState, updateFormState, sharedListId, formConfig?.id],
  );

  const updateField = useCallback(
    (fieldId, updates) => {
      if (!formState) return;

      updateFormState({
        fields: formState.fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
      });
    },
    [formState, updateFormState],
  );

  const deleteField = useCallback(
    (fieldId) => {
      if (!formState) return;

      const fieldToDelete = formState.fields.find((f) => f.id === fieldId);

      if (fieldToDelete?.systemLocked) return;

      updateFormState({
        fields: formState.fields.filter((f) => f.id !== fieldId).map((f, i) => ({ ...f, order: i })),
      });

      if (fieldToDelete) {
        trackEvent('contacts:list:signup_form_field_removed', {
          field_type: fieldToDelete.type,
          shared_list_id: sharedListId,
          form_id: formConfig?.id,
        });
      }
    },
    [formState, updateFormState, sharedListId, formConfig?.id],
  );

  const reorderFields = useCallback(
    (sourceIndex, destIndex) => {
      if (!formState) return;

      const reordered = [...formState.fields];
      const [removed] = reordered.splice(sourceIndex, 1);
      reordered.splice(destIndex, 0, removed);
      updateFormState({
        fields: reordered.map((f, i) => ({ ...f, order: i })),
      });
    },
    [formState, updateFormState],
  );

  const updateHeader = useCallback(
    (field, value) => {
      updateFormState({ [field]: value });
    },
    [updateFormState],
  );

  const updateSubmitButton = useCallback(
    (text) => {
      updateFormState({ submitButtonText: text });
    },
    [updateFormState],
  );

  const updateSuccessMessage = useCallback(
    (field, value) => {
      updateFormState({ [field]: value });
    },
    [updateFormState],
  );

  // Save to server
  const saveForm = useCallback(async () => {
    if (!formState) return { success: false, error: 'No form state' };

    // Transform fields to API format
    // Only include id for existing fields (numeric ids from API), not for new fields (UUID ids)
    const transformedFields = formState.fields.map((field) => ({
      ...(typeof field.id === 'number' ? { id: field.id } : {}),
      name: field.label,
      field_type: field.type,
      required: field.required,
      ...(field.type === 'select' || field.type === 'checkbox'
        ? { options: field.options, allowMultiple: field.allowMultiple }
        : {}),
    }));

    try {
      await updateConfig({
        collectionId,
        sharedListId,
        formId: formConfig?.id,
        title: formState.title,
        description: formState.description,
        active: true,
        fields: transformedFields,
        successMessageTitle: formState.successMessageTitle,
        successMessageDescription: formState.successMessageDescription,
        submitButtonText: formState.submitButtonText,
      }).unwrap();
      setIsDirty(false);

      trackEvent('contacts:list:signup_form_saved', {
        shared_list_id: sharedListId,
        form_id: formConfig?.id,
        field_count: formState.fields.length,
      });

      return { success: true };
    } catch (err) {
      return { success: false, error: err };
    }
  }, [formState, collectionId, sharedListId, formConfig?.id, updateConfig]);

  const resetForm = useCallback(() => {
    if (formConfig) {
      const fields = transformApiFieldsToInternal(formConfig.signupFormFields);
      setFormState({
        title: formConfig.title || '',
        description: formConfig.description || '',
        fields,
        submitButtonText: formConfig.submitButtonText || 'Join now',
        successMessageTitle: formConfig.successMessageTitle || 'Thanks for joining!',
        successMessageDescription:
          formConfig.successMessageDescription || "We'll get in touch when there are relevant opportunities.",
      });
      setIsDirty(false);
    }
  }, [formConfig]);

  const clearLastAddedFieldId = useCallback(() => {
    setLastAddedFieldId(null);
  }, []);

  return {
    formState,
    isLoading,
    isSaving,
    isDirty,
    activeTab,
    error,
    lastAddedFieldId,
    setActiveTab,
    addField,
    updateField,
    deleteField,
    reorderFields,
    updateHeader,
    updateSubmitButton,
    updateSuccessMessage,
    saveForm,
    resetForm,
    clearLastAddedFieldId,
  };
}
